import { useMemo, useEffect, useContext } from "react";
import { FeatureType } from "../../constants/FeatureToggles";
import { ClaimsContext } from "../../services/ClaimsService";
import { useLocation } from "react-router-dom";

import "./Help.scss";

import { HELP_DATA } from "./constants";
import BreadCrumb from "../../common/breadCrumb/BreadCrumb";
import { Link, useNavigate } from "react-router-dom";
import book from "./book.png";
import library from "./library.png";
import mail from "./mail.png";
import play from "./play.png";
import userAvatar from "../../images/img_avatar1.png";

// Make AbsCard use a simple isEnabled prop, no context/feature logic inside
const AbsCard = ({ cardData, onClick, isEnabled }) => {
    const { cardHeader, cardDescription, cardClr, cardType, img } = cardData || {};
    const icon =
        img ??
        (cardType === "enroll_vessel"
            ? play
            : cardType === "reports"
            ? book
            : cardType === "portcodes"
            ? library
            : mail);

    if (!isEnabled) {
        return null;
    }

    return (
        <div
            className="card"
            onClick={onClick}
            style={onClick ? { cursor: "pointer" } : undefined}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
        >
            <div className="dashboard-card-container text-center">
                <div className="dashboard-card-header">{cardHeader}</div>
                <div
                    className="d-card-container d-flex justify-content-center align-items-center"
                    style={{ backgroundColor: cardClr || "#F5F7FA" }}
                >
                    <img src={icon} alt={cardHeader || "card-icon"} />
                </div>
                <div className="mt-3">
                    <div className="dashboard-card-description">{cardDescription}</div>
                </div>
            </div>
        </div>
    );
};

const HelpPage = () => {
    const breadcrumb = useMemo(
        () => [
            {
                label: HELP_DATA["nav-support-text"],
                url: "",
                navId: "support_navId",
            },
            {
                label: HELP_DATA["nav-help-text"],
                url: "",
                navId: "help_navId",
            },
        ],
        []
    );

    const navigate = useNavigate();

    // Feature Toggle
    const location = useLocation();
    const wcnNumber = location.state?.wcnNumber;

    const { userHasFeature, userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta } =
        useContext(ClaimsContext);

    useEffect(() => {
        if (!userFeaturesAreLoaded || checkWcnDelta(wcnNumber)) {
            fetchUserFeatures(wcnNumber);
        }
    }, [userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta, wcnNumber]);

    const showVideoTutorialsCard = useMemo(() => userHasFeature(FeatureType.ViewSupportVideoTutorials), [userHasFeature]);
    const showGuidesCard = useMemo(() => userHasFeature(FeatureType.ViewSupportGuides), [userHasFeature]);
    const showResourcesCard = useMemo(() => userHasFeature(FeatureType.ViewSupportResources), [userHasFeature]);
    const showContactInformationCard = useMemo(() => userHasFeature(FeatureType.ViewSupportContact), [userHasFeature]);

    const shouldRenderAny =
        showVideoTutorialsCard ||
        showGuidesCard ||
        showResourcesCard ||
        showContactInformationCard;

    const dashboardCards = useMemo(
        () => ({
            videoTutorialsCard: {
                cardHeader: "Video Tutorials",
                cardDescription: "Quick videos to guide you through key features",
                cardClr: "#DAE9FC",
                cardType: "enroll_vessel",
                img: play,
            },
            guidesCard: {
                cardHeader: "Guides",
                cardDescription: "Step-by-step User Instructions",
                cardClr: "#E8E5FF",
                cardType: "portcodes",
                img: book,
            },
            resourcesCard: {
                cardHeader: "Resources",
                cardDescription: "Helpful links and materials at your fingertips",
                cardClr: "#FFE2E7",
                cardType: "reports",
                img: library,
            },
            contactInformationCard: {
                cardHeader: "Contact Information",
                cardDescription: "Find contact details for your local support office",
                cardClr: "#F5F7FA",
                cardType: "child_reports",
                img: mail,
            },
        }),
        []
    );

    return (
        <div className="help-container col-12 py-3 px-4">
            <BreadCrumb items={breadcrumb} />

            {/* Support cards */}
            <div className="d-flex justify-content-left erp-cards card-container-div row mb-3">
                {showVideoTutorialsCard && (
                    <div className="collapsible-card-div col-lg-3 col-md-6 mb-3">
                        <AbsCard
                            cardData={dashboardCards.videoTutorialsCard}
                            isEnabled={showVideoTutorialsCard}
                            onClick={() => navigate("/video-tutorials")}
                        />
                    </div>
                )}
                {showGuidesCard && (
                    <div className="collapsible-card-div col-lg-3 col-md-6 mb-3">
                        <AbsCard
                            cardData={dashboardCards.guidesCard}
                            isEnabled={showGuidesCard}
                            onClick={() => navigate("/guides")}
                        />
                    </div>
                )}
                {showResourcesCard && (
                    <div className="collapsible-card-div col-lg-3 col-md-6 mb-3">
                        <AbsCard
                            cardData={dashboardCards.resourcesCard}
                            isEnabled={showResourcesCard}
                            onClick={() => navigate("/resources")}
                        />
                    </div>
                )}
                {showContactInformationCard && (
                    <div className="collapsible-card-div col-lg-3 col-md-6 mb-3">
                        <AbsCard
                            cardData={dashboardCards.contactInformationCard}
                            isEnabled={showContactInformationCard}
                            onClick={() => navigate("/contacts")}
                        />
                    </div>
                )}
            </div>

            {/* Old UI if feature toggles are all disabled */}
            {!shouldRenderAny && (
                <>
                    <div className="mb-3">
                        <Link to={"/video-tutorials"} className="btn help-button mr-2" rel="noreferrer">
                            {HELP_DATA["video-tutorials-btntext"]}
                        </Link>
                    </div>

                    <div className="row">
                        <div className="col-lg-12 mb-3">
                            <div className="card">
                                <div className="card-header">
                                    <h6>{HELP_DATA["support-contacts"]}</h6>
                                </div>
                                <div className="card-body">
                                    <div className="row col-lg-12">
                                        <div className="card mr-2">
                                            <div className="card-body p-0">
                                                <div className="d-flex text-black">
                                                    <div className="flex-shrink-0">
                                                        <img
                                                            className="img-fluid"
                                                            src={userAvatar}
                                                            alt="user-avatar"
                                                            style={{ width: "120px" }}
                                                        />
                                                    </div>
                                                    <div className="flex-grow-1 ms-3 p-2 pl-3 pr-5">
                                                        <h6 className="mb-1">
                                                            {HELP_DATA["general"]}
                                                        </h6>
                                                        <span className="text-sm">
                                                            {HELP_DATA["global-support"]}
                                                        </span>
                                                        <br />
                                                        <span className="text-sm">
                                                            <i className="fa fa-envelope"></i>
                                                            <a
                                                                href="mailto:abserp@eagle.org"
                                                                className="hyperlink"
                                                            >
                                                                {HELP_DATA["help-email"]}
                                                            </a>
                                                        </span>
                                                        <br />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h6>{HELP_DATA["FAQ-more"]}</h6>
                                </div>
                                <div className="card-body">
                                    <ul className="list-unstyled">
                                        <li>
                                            {HELP_DATA["submit-thetis"]}
                                            <a
                                                href={HELP_DATA["EU-MRV-link1"]}
                                                target="_blank"
                                                className="hyperlink"
                                                rel="noreferrer"
                                            >
                                                {HELP_DATA["EU-MRV-link1"]}
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default HelpPage;
